import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ICreateSessionData,
  IEditSessionData,
  ISession,
  ISessionLimit,
  Method,
  TCompletedStepRecord,
  TCreateTrustpilotInvitationLink,
  TTrustpilotInvitationLink,
} from 'common-types';
import { TConfig } from 'common-types/config';
import { TContentMembershipV2 } from 'common-types/membership';
import { TLikeOutfitData, TOutfit, TSavedOutfit } from 'common-types/outfits';
import { useLocalStorageStore } from 'store';
import { APP_TYPE } from 'utils';

import { CHIC_URL } from './config';

const axiosInstance: AxiosInstance = axios.create({
  baseURL: CHIC_URL,
});

class ChicApi {
  private client = axiosInstance;

  private setGlobalHeader(headerName: string, value: string): void {
    axios.defaults.headers.common[headerName] = value;
  }

  private request = async <T>(url: string, method: Method, data?: unknown): Promise<T> => {
    const token = useLocalStorageStore.getState().token;
    const userId = useLocalStorageStore.getState().userId;
    const response = await axios.request<T>({
      url: `${CHIC_URL}${url}`,
      method,
      data,
      headers: {
        'User-Id': userId,
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  };

  public setAuth = (token: string): void => {
    this.setGlobalHeader('Authorization', `Bearer ${token}`);
  };

  public addSession = async (data: ICreateSessionData): Promise<unknown> => {
    return this.request('/sessions', Method.POST, { ...data, app: APP_TYPE });
  };

  public editSession = async (id: string, data: IEditSessionData): Promise<unknown> => {
    return this.request(`/sessions/${id}`, Method.PUT, data);
  };

  public getLastUserSession = async (userId: string): Promise<ISession> => {
    return this.request(`/sessions/user/${userId}`, Method.GET);
  };

  public getSessionLimit = async (userId: string): Promise<ISessionLimit[]> => {
    return this.request(`/sessions/${userId}/limits`, Method.GET);
  };

  public setSessionLimit = async (userId: string, data: ISessionLimit): Promise<unknown> => {
    return this.request(`/user-session-limit/set/${userId}`, Method.POST, data);
  };

  public getOutfit = async (id: string, userId?: string): Promise<TOutfit> => {
    const url = userId ? `/outfit/${id}/public?userId=${userId}` : `/outfit/${id}/public`;

    return this.request(url, Method.GET);
  };

  public getSavedOutfits = async (userId: string): Promise<TSavedOutfit[]> => {
    return this.request(`/users/${userId}/outfits`, Method.GET);
  };

  public getConfig = async (): Promise<TConfig> => {
    return this.request('/config', Method.GET);
  };

  public getContentMembershipV2 = async (signal?: AbortSignal): Promise<TContentMembershipV2> => {
    return this.request('/content/membershipV2', Method.GET, { signal });
  };

  public likeOutfit = async (data: TLikeOutfitData): Promise<AxiosResponse> => {
    return this.request('/outfit/like', Method.PUT, { ...data, app: APP_TYPE });
  };

  public setMaxMembershipLevel = async (userId: string): Promise<void> => {
    return this.request('/user-membership/max-level', Method.POST, { userId });
  };

  public getCompletedSteps = async (): Promise<TCompletedStepRecord[]> => {
    const userId = useLocalStorageStore.getState().userId;
    return this.request(`/user-journey/step?filter=${JSON.stringify({ userId })}`, Method.GET);
  };

  public addCompletedStep = async (journeyStep: string): Promise<TCompletedStepRecord> => {
    const userId = useLocalStorageStore.getState().userId;
    return this.request('/user-journey/step', Method.POST, { userId, journeyStep });
  };

  public deleteCompletedStep = async (journeyStepId: string): Promise<void> => {
    return this.request(`/user-journey/step/${journeyStepId}`, Method.DELETE);
  };

  public createTrustpilotInvitationLink = async (
    payload: TCreateTrustpilotInvitationLink,
  ): Promise<TTrustpilotInvitationLink> => {
    const data = await this.request<TTrustpilotInvitationLink>('/trustpilot/invitation-link', Method.POST, payload);

    return data;
  };
}

const chicApi = new ChicApi();

export default chicApi;
